import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage.service';
import { AuthenticationService } from '@akebono/core';
import { PERMISSION_ADMIN, PERMISSION_SECURITY_BYPASS } from 'src/app/const';
import { combineLatest, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SecurityEvent } from './types/security-event.type';

@Injectable({
  providedIn: 'root',
})
export class SecurityStateService {
  private readonly forceSecurityEnabledLocalStorageKey = '_fse';

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  async isEnabled(type: SecurityEvent['eventType']): Promise<boolean> {
    const isBypass = await this.isUserHasBypassPermission();
    const isProduction = this.isProductionEnvironment;
    const isForceEnabled = this.isForceSecurityEnabled(type);

    return isForceEnabled || (isProduction && !isBypass);
  }

  private isUserHasBypassPermission(): Promise<boolean> {
    const hasAdminPermission$ = this.authenticationService.checkPermissions([PERMISSION_ADMIN]);
    const hasBypassPermission$ = this.authenticationService.checkPermissions([
      PERMISSION_SECURITY_BYPASS,
    ]);

    return combineLatest([hasAdminPermission$, hasBypassPermission$])
      .pipe(
        first(),
        map((hasPermissions) => hasPermissions.some((p) => p)),
        catchError(() => of(false)),
      )
      .toPromise();
  }

  private get isProductionEnvironment(): boolean {
    const { staging, production } = environment;

    return !staging && production;
  }

  private isForceSecurityEnabled(type: SecurityEvent['eventType']): boolean {
    const fse = this.localStorageService.get(this.forceSecurityEnabledLocalStorageKey);

    if (typeof fse === 'boolean') {
      return fse;
    } else if (Array.isArray(fse)) {
      return fse.includes(type);
    } else {
      return false;
    }
  }
}
